import getAllSharePointLinks from '../utils/getAllSharePointLinks';
import getSharingInfoToTryUpdateLink from '../utils/getSharingInfoToTryUpdateLink';
import handleBeforePasteEvent from '../utils/handleBeforePasteEvent';
import isLinkRemovalRelatedEvent from '../utils/isLinkRemovalRelatedEvent';
import isReadonlyBeautifulLink from '../utils/isReadonlyBeautifulLink';
import processContentChangedEvent from '../utils/processContentChangedEvent';
import { removeComposeOnlyContent } from '../utils/removeComposeOnlyContent';
import trySetIdAndClassName from '../utils/trySetIdAndClassName';
import { AttachmentWellViewState } from 'owa-attachment-well-data';
import { EditorPluginEx, PluginUtils } from 'owa-editor';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import { getNumberOfSharingLinks, getSharingLink, SharingLinkInfo } from 'owa-link-data';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import ScrollAdapter from 'owa-scroll-adapter/lib/ScrollAdapter';
import { isSharePointLink } from 'owa-sharepoint-link-detector';
import { trace } from 'owa-trace';
import { Editor } from 'roosterjs-editor-core';
import { PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import { PositionType } from 'roosterjs-editor-types';
import {
    getComposeLinkViewState,
    isContextMenuOpen,
    removeAllComposeLinkViewStates,
    closeLinkContextMenu,
    showLinkContextMenu,
} from 'owa-compose-link';
import {
    BeforePasteEvent,
    ChangeSource,
    ContentChangedEvent,
    ExtractContentEvent,
} from 'roosterjs-editor-types';

const RESIZE_EVENT_NAME = 'resize';

export default class LinkPlugin implements EditorPluginEx {
    public editor: Editor;
    private utils: PluginUtils;
    private pendingSPLinksTask;
    private onCutDisposer: () => void;

    // This is for handling stuck spinner from Ctrl+Z undo
    // If we can't find the link Id in this array, we would remove the spinner on event contentChange from setContent.
    private linkIdsWithSpinner: string[];

    constructor(
        private viewState: AttachmentWellViewState,
        private scrollAdapter: ScrollAdapter,
        public recipientWells: RecipientWellWithFindControlViewState[],
        public fromAddress: string
    ) {
        this.linkIdsWithSpinner = [];
    }

    public getName() {
        return 'Link';
    }

    public initialize(editor: Editor): void {
        this.editor = editor;
        this.onCutDisposer = this.editor.addDomEventHandler('cut', this.tryGetAllSPLinks);
        this.scrollAdapter.addScrollEventListener(this.onScrollOrResize);
        window.addEventListener(RESIZE_EVENT_NAME, this.onScrollOrResize);
    }

    public setPluginUtils(utils: PluginUtils) {
        this.utils = utils;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.BeforePaste) {
            // Handles pasting text on the editor (but not html blob)
            handleBeforePasteEvent(
                <BeforePasteEvent>event,
                this,
                this.viewState,
                this.linkIdsWithSpinner
            );
        } else if (event.eventType === PluginEventType.ContentChanged) {
            // Handles adding an hyperlink using the hyperlink button in compose or pasting html blob
            const contentChangedEvent: ContentChangedEvent = event as ContentChangedEvent;
            if (
                (contentChangedEvent.source === ChangeSource.AutoLink ||
                    contentChangedEvent.source === ChangeSource.CreateLink) &&
                contentChangedEvent.data &&
                contentChangedEvent.data instanceof HTMLAnchorElement
            ) {
                if (!isSharePointLink(contentChangedEvent.data.href)) {
                    return;
                }

                trace.info('LinkPlugin.handleLink');
                trySetIdAndClassName(contentChangedEvent.data, true /*shouldChangeLinkId*/);
                getSharingInfoToTryUpdateLink(
                    this,
                    contentChangedEvent.source === ChangeSource.AutoLink /* shouldBeautifyLink */,
                    true /* shouldRehydrate  */,
                    false /* isPreviouslyBeautified */,
                    contentChangedEvent.data,
                    this.viewState,
                    0 /* linkIndex */,
                    this.linkIdsWithSpinner,
                    true /*shouldTryShowContextMenuOnMount*/
                );
            } else {
                // Handles loading from draft, reply, reply all and fwd, undo
                processContentChangedEvent(event, this, this.viewState, this.linkIdsWithSpinner);
            }
        } else if (event.eventType === PluginEventType.ExtractContent) {
            const extractContentEvent = event as ExtractContentEvent;
            extractContentEvent.content = removeComposeOnlyContent(extractContentEvent.content);
            return;
        } else if (
            event.eventType === PluginEventType.KeyUp ||
            event.eventType === PluginEventType.MouseUp
        ) {
            const selectionNode = this.editor.getSelectionRange();
            if (selectionNode) {
                const startNode: Node = selectionNode.startContainer;
                const endNode: Node = selectionNode.endContainer;
                const parentNode: Node = startNode.parentNode;
                const anchorElement = parentNode as HTMLAnchorElement;

                if (
                    parentNode.nodeName.toLowerCase() === 'a' &&
                    startNode === endNode &&
                    this.isSharingLink(anchorElement.id, anchorElement.href) &&
                    !!getComposeLinkViewState(anchorElement.id)
                ) {
                    if (!isContextMenuOpen(anchorElement.id)) {
                        showLinkContextMenu(
                            anchorElement,
                            this.editor,
                            this.viewState.composeId,
                            this.recipientWells,
                            this.fromAddress
                        );
                    }
                } else {
                    closeLinkContextMenu();

                    if (startNode === endNode && startNode.nodeName.toLowerCase() === 'a') {
                        const anchor = startNode as HTMLAnchorElement;
                        if (
                            isReadonlyBeautifulLink(anchor) &&
                            selectionNode.startOffset === selectionNode.endOffset
                        ) {
                            // Sometimes cursor is trapped inside the readonly beautiful link, where edit won't work.
                            // So we have to move it outside the readonly link for edit to work
                            if (selectionNode.startOffset !== startNode.childNodes.length) {
                                trace.info('move select before the anchor element');
                                this.editor.select(anchor, PositionType.Before);
                            } else {
                                trace.info('move select after the anchor element');
                                this.editor.select(anchor, PositionType.After);
                            }
                        }
                    }
                }
            }
        } else if (
            event.eventType === PluginEventType.KeyDown &&
            event.rawEvent.which === KeyboardCharCodes.Backspace
        ) {
            // Handling the backspace at the end of readonly beautiful link to remove it.
            const searcher = this.utils.cacheGetContentSearcher(event, this.editor);
            const inline = searcher.getInlineElementBefore();
            const selectionNode = this.editor.getSelectionRange();
            if (
                inline &&
                selectionNode &&
                selectionNode.startContainer === selectionNode.endContainer &&
                selectionNode.startOffset === selectionNode.endOffset
            ) {
                const containerNode: Node = inline.getContainerNode();
                if (isReadonlyBeautifulLink(containerNode)) {
                    this.editor.deleteNode(containerNode);
                    this.utils.clearContentSearcherCache(event);
                    event.rawEvent.preventDefault();
                }
            }
        }

        this.processLinkRemovalRelatedEvents(event);
    }

    private processLinkRemovalRelatedEvents(event: PluginEvent): void {
        if (isLinkRemovalRelatedEvent(event, this.editor)) {
            this.tryGetAllSPLinks();
        }
    }

    private tryGetAllSPLinks = () => {
        if (!this.pendingSPLinksTask) {
            this.pendingSPLinksTask = setTimeout(() => {
                this.getAllSPLinks();
            }, 500);
        }
    };

    private getAllSPLinks(): void {
        trace.info('processSPLinks');
        this.pendingSPLinksTask = null;
        getAllSharePointLinks(this.editor, this.viewState);
    }

    private onScrollOrResize = () => {
        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();
        }
    };

    private isSharingLink(anchorId: string, href: string): boolean {
        if (!anchorId || anchorId === '') {
            return false;
        }

        const sharingLink: SharingLinkInfo = getSharingLink(anchorId);
        if (sharingLink) {
            return sharingLink.url === href;
        }

        return false;
    }

    public dispose(): void {
        if (this.onCutDisposer) {
            this.onCutDisposer();
            this.onCutDisposer = null;
        }

        if (this.pendingSPLinksTask) {
            clearTimeout(this.pendingSPLinksTask);
            this.pendingSPLinksTask = null;
        }

        this.editor = null;
        this.scrollAdapter.removeScrollEventListener(this.onScrollOrResize);
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScrollOrResize);

        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();
            removeAllComposeLinkViewStates();
        }
    }
}
